import { Box, Chip, Divider, Menu, MenuItem, SvgIcon, Typography } from '@mui/material'
import { parseISO } from 'date-fns'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import DOMPurify from 'dompurify'

import { useSiteConfiguration } from '@hapana/shared/web/hooks/siteConfiguration.hooks'
import { defaultTimeZoneFormat } from '@hapana/shared/utils/date.util'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'

import { styles } from './notes-properties.styles'
import ModalOpenNote from './modals/modal-open-note.component'
import { useCallback, useState } from 'react'
import PenIcon from '@hapana/shared/web/styles/assets/svgs/pen-small.svg'
import DeleteIcon from '@hapana/shared/web/styles/assets/svgs/trash-small.svg'
import OpenNoteIcon from '@hapana/shared/web/styles/assets/svgs/note-small.svg'
import OptionIcon from '@hapana/shared/web/styles/assets/svgs/ellipsis-v.svg'
import AddEditNote from './modals/modal-add-edit-note.component'
import { AUTOCOMPLETE_VALUES as ALERTS_MOCK_DATA } from '../../form/form.constants'
import { CHIP_SIZE, CHIP_VARIANT } from '../../chip/chip.constants'
import ModalDeleteNote from './modals/modal-delete-note.component'

const NoteWithBadge = ({ edited, badgeEdited, badge, date, description }) => {
  const { data } = useSiteConfiguration()
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const { pushDialog } = useDialog()
  const { t } = useTranslation(['checkIn'])

  const renderNoteActionMenu = () => {
    return (
      <Menu anchorEl={anchorEl} data-testid="noteActionMenu" open={open} onClose={handleClose}>
        <MenuItem
          onClick={() => {
            pushDialog(AddEditNote, {
              showEditTitle: true,
              description,
              name: ALERTS_MOCK_DATA.username,
            })
          }}
        >
          <SvgIcon component={PenIcon} sx={styles.editIcon} />
          <Typography typography="medium" color="gray.80" sx={styles.noteActionOption}>
            {t('checkIn:alerts.notesAndPropertiesAlerts.actionToNote.editNote')}
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            pushDialog(ModalOpenNote, {
              title: t('checkIn:alerts.title'),
              description,
              name: ALERTS_MOCK_DATA.username,
            })
          }}
        >
          <SvgIcon component={OpenNoteIcon} sx={styles.openNote} />
          <Typography typography="medium" color="gray.80" sx={styles.noteActionOption}>
            {t('checkIn:alerts.notesAndPropertiesAlerts.actionToNote.openNote')}
          </Typography>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            pushDialog(ModalDeleteNote)
          }}
        >
          <SvgIcon component={DeleteIcon} sx={styles.deleteNote} />
          <Typography typography="medium" color="gray.80" sx={styles.noteActionOption}>
            {t('checkIn:alerts.notesAndPropertiesAlerts.actionToNote.deleteNote')}
          </Typography>
        </MenuItem>
      </Menu>
    )
  }

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const formattedDate = defaultTimeZoneFormat(parseISO(date), data?.timezone)

  const handleViewNote = useCallback(() => {
    pushDialog(ModalOpenNote, {
      title: t('checkIn:alerts.title'),
      description,
      date: formattedDate,
    })
  }, [description, formattedDate, pushDialog, t])

  const sanitizedHtml = { __html: DOMPurify.sanitize(description) }

  return (
    <>
      <Box sx={styles.notewithBadgeContainer}>
        <Box>
          <Box sx={styles.noteDetailContainer}>
            <Typography typography="header4" color="gray.80" sx={styles.date}>
              {formattedDate}
            </Typography>
            <Box sx={styles.chipContainer}>
              <Box type="span" sx={styles.chipStyle}>
                {badge && <Chip label={badge} size={CHIP_SIZE.SMALL} />}
              </Box>
              {edited && (
                <Chip
                  label={badgeEdited}
                  size={CHIP_SIZE.SMALL}
                  variant={CHIP_VARIANT.FILTER}
                  disabled
                />
              )}
            </Box>
          </Box>
        </Box>
        <SvgIcon
          component={OptionIcon}
          onClick={handleClick}
          id="note-option-icon"
          sx={styles.optionIcon}
        />
        {renderNoteActionMenu()}
      </Box>
      <Typography
        typography="medium"
        color="gray.80"
        sx={styles.noteHtmlClamp}
        dangerouslySetInnerHTML={sanitizedHtml}
      />
    </>
  )
}

NoteWithBadge.propTypes = {
  id: PropTypes.string,
  edited: PropTypes.bool,
  badgeEdited: PropTypes.string,
  badge: PropTypes.string,
  date: PropTypes.string,
  description: PropTypes.string,
}

NoteWithBadge.defaultProps = {
  id: '',
  edited: false,
  badgeEdited: '',
  badge: '',
  date: '',
  description: '',
}

export default NoteWithBadge
